import { Injectable } from '@angular/core';

/** One event's two share codes: `c` the collaborator hash, `v` the view-only one. */
interface StoredCodes {
  c?: string;
  v?: string;
  at: number;
}

export interface InviteCodes {
  collab: string;
  viewer: string;
}

const STORAGE_KEY = 'invite_codes';
/** Enough for every event a host is likely to reopen; the oldest drop off. */
const MAX_EVENTS = 40;
const CODE_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;

/**
 * The two codes behind an event's invite links, kept per event id so the
 * invite screen can build both links the moment it opens instead of waiting
 * on pwa_get_event_qr (which generates up to eight QR images before it
 * answers, and has hung outright — 2026-10-23, every call timing out at 6 s).
 *
 * A link is only ever `https://i.picaggo.com/{code}`. The code is the part the
 * browser can't make up — the server mints it when the event is created — so
 * this keeps every copy the app is handed: the create response, the
 * event-detail response (host only) and any getEventQR answer for an event
 * that hasn't ended. A past event's getEventQR codes expire, so they aren't
 * kept; the event's own hash, which never does, is.
 *
 * Keyed by event id, unlike the old `event_hash` / `viewer_hash` keys, which
 * every event shares and other screens overwrite.
 */
@Injectable({ providedIn: 'root' })
export class InviteCodeStore {

  get(eventId: any): InviteCodes {
    const entry = this.read()[String(eventId)];
    return { collab: entry?.c || '', viewer: entry?.v || '' };
  }

  /** Keeps whichever of the two codes are real; a missing one leaves the stored one alone. */
  remember(eventId: any, collab?: string | null, viewer?: string | null): void {
    if (eventId == null || eventId === '') return;
    const c = InviteCodeStore.valid(collab);
    const v = InviteCodeStore.valid(viewer);
    if (!c && !v) return;

    const all = this.read();
    const key = String(eventId);
    const prev = all[key];
    all[key] = { c: c || prev?.c, v: v || prev?.v, at: Date.now() };

    const keys = Object.keys(all);
    if (keys.length > MAX_EVENTS) {
      keys.sort((a, b) => all[a].at - all[b].at)
        .slice(0, keys.length - MAX_EVENTS)
        .forEach(k => delete all[k]);
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch {
      // Storage full or blocked: the screen falls back to fetching.
    }
  }

  /** Strings like "undefined" have been stored as codes before; never hand one out. */
  private static valid(code?: string | null): string {
    const s = code == null ? '' : String(code).trim();
    return CODE_PATTERN.test(s) && s !== 'undefined' && s !== 'null' ? s : '';
  }

  private read(): Record<string, StoredCodes> {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
}
